import { Box, Typography, Button, Chip, IconButton, useTheme } from "@mui/material";
import Battery0BarIcon from "@mui/icons-material/Battery0Bar";
import Battery1BarIcon from "@mui/icons-material/Battery1Bar";
import Battery2BarIcon from "@mui/icons-material/Battery2Bar";
import Battery3BarIcon from "@mui/icons-material/Battery3Bar";
import Battery4BarIcon from "@mui/icons-material/Battery4Bar";
import Battery5BarIcon from "@mui/icons-material/Battery5Bar";
import Battery6BarIcon from "@mui/icons-material/Battery6Bar";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BatteryUnknownIcon from "@mui/icons-material/BatteryUnknown";
import CloseIcon from "@mui/icons-material/Close";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import NavigationIcon from "@mui/icons-material/Navigation";
import HistoryIcon from "@mui/icons-material/History";
import BusinessIcon from "@mui/icons-material/Business";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// This component has a css file you can use to get rid of the borders of Google's Info window component.
// To enable the css, the parent of the map component needs to have the className "custom-css"

export default function PopUpMarkerContent(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  // Theme-aware colors
  const bgPrimary = isDark ? "#032d42" : "#ffffff";
  const bgSecondary = isDark ? "#021e2d" : "#f8fafc";
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.15)" : "rgba(0, 0, 0, 0.08)";
  const iconBg = isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)";

  const {
    buttonLabel = "View Details",
    clickedFacility = false,
    facility = {},
    item = {},
    onClose = null,
    onButtonClick = () => {},
    onButtonClick2 = () => {},
    onNavigateButtonClick = () => {},
  } = props;

  const {
    assetTag = null,
    event = null,
    facility: facilityFromAsset = null,
    lastEvent = null,
    string = null,
    tag = null,
    // Actual data fields - use if available
    batteryLevel = null,
    batteryLife = null,
    battery = null,
    temperature = null,
    temp = null,
    timeStamp = null,
    lastSeen = null,
    latitude = null,
    longitude = null,
  } = item;

  const { name: facilityName = "" } = facility;

  // Get actual battery level from various possible field names
  const actualBattery = batteryLevel ?? batteryLife ?? battery ?? null;
  
  // Get actual temperature from various possible field names
  const actualTemperature = temperature ?? temp ?? null;

  // Get battery icon based on level
  const getBatteryIcon = (level) => {
    if (level === null || level === undefined) return <BatteryUnknownIcon />;
    if (level <= 10) return <Battery0BarIcon />;
    if (level <= 20) return <Battery1BarIcon />;
    if (level <= 35) return <Battery2BarIcon />;
    if (level <= 50) return <Battery3BarIcon />;
    if (level <= 65) return <Battery4BarIcon />;
    if (level <= 80) return <Battery5BarIcon />;
    if (level <= 95) return <Battery6BarIcon />;
    return <BatteryFullIcon />;
  };

  // Get battery color based on level
  const getBatteryColor = (level) => {
    if (level === null || level === undefined) return textSecondary;
    if (level <= 20) return "#ef4444";
    if (level <= 50) return "#f59e0b";
    return greenColor;
  };

  // Format location string
  const locationString = [facilityFromAsset, string].filter(Boolean).join(", ") || "Unknown Location";

  // Compact info row for dashboard popup
  const InfoRow = ({ icon, label, value, valueColor, chip }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "4px 0",
        borderBottom: `1px solid ${borderColor}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          width: "24px",
          height: "24px",
          borderRadius: "6px",
          backgroundColor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& .MuiSvgIcon-root": {
            fontSize: "0.75rem",
            color: greenColor,
          },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.625rem",
            color: textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        {chip ? (
          <Chip
            label={value}
            size="small"
            sx={{
              mt: 0.25,
              height: "18px",
              fontSize: "0.6875rem",
              fontWeight: 600,
              backgroundColor: valueColor ? `${valueColor}20` : iconBg,
              color: valueColor || textPrimary,
              border: `1px solid ${valueColor ? `${valueColor}40` : borderColor}`,
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: valueColor || textPrimary,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Compact action button for dashboard popup
  const ActionButton = ({ icon, label, onClick, variant = "outlined" }) => (
    <Button
      onClick={onClick}
      variant={variant}
      size="small"
      startIcon={icon}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: "6px",
        textTransform: "none",
        fontSize: "0.6875rem",
        fontWeight: 600,
        padding: "5px 8px",
        "& .MuiSvgIcon-root": { fontSize: "0.8rem" },
        ...(variant === "contained" ? {
          backgroundColor: greenColor,
          color: "#ffffff",
          border: "none",
          "&:hover": {
            backgroundColor: "#8ab45a",
          },
        } : {
          borderColor: borderColor,
          color: textPrimary,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
            borderColor: greenColor,
          },
        }),
      }}
    >
      {label}
    </Button>
  );

  // ===== FACILITY POPUP (compact, no scroll) =====
  if (clickedFacility) {
    return (
      <Box
        className="map-info-window map-popup-compact"
        sx={{
          backgroundColor: bgPrimary,
          borderRadius: "12px",
          overflow: "hidden",
          minWidth: "200px",
          maxWidth: "260px",
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.3)"
            : "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        {/* Compact header with close */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${navyColor} 0%, #1a4a6a 100%)`,
            padding: "4px 8px 5px",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: `linear-gradient(90deg, ${greenColor}, transparent)`,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minHeight: 24 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "6px",
                backgroundColor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <BusinessIcon sx={{ color: "#fff", fontSize: "0.75rem" }} />
            </Box>
            <Typography
              sx={{
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {facilityName || "Facility"}
            </Typography>
            {onClose && (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close"
                sx={{
                  padding: "2px",
                  color: "rgba(255,255,255,0.85)",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", color: "#fff" },
                }}
              >
                <CloseIcon sx={{ fontSize: "0.875rem" }} />
              </IconButton>
            )}
          </Box>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.6rem", fontWeight: 500, mt: 0.25 }}>
            Facility Location
          </Typography>
        </Box>

        {/* Compact content */}
        <Box sx={{ padding: "6px 8px 8px" }}>
          {Object.values(item).length > 0 && (
            <Box
              sx={{
                backgroundColor: bgSecondary,
                borderRadius: "6px",
                padding: "6px 8px",
                marginBottom: "6px",
                border: `1px solid ${borderColor}`,
              }}
            >
              <Typography sx={{ fontSize: "0.65rem", color: textSecondary }}>Asset Found</Typography>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: greenColor, lineHeight: 1.25 }}>
                {tag || assetTag || "Unknown Asset"}
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: textSecondary, mt: 0.25 }}>is located in this facility</Typography>
            </Box>
          )}

          {/* Actions in one row */}
          <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            <ActionButton icon={<OpenInNewIcon />} label={buttonLabel} onClick={onButtonClick} variant="contained" />
            <ActionButton icon={<NavigationIcon />} label="Navigate" onClick={() => onNavigateButtonClick(facility)} />
            {Object.values(item).length > 0 && (
              <ActionButton icon={<HistoryIcon />} label="History" onClick={onButtonClick2} />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ===== ASSET POPUP (compact, no scroll) =====
  return (
    <Box
      className="map-info-window map-popup-compact"
      sx={{
        backgroundColor: bgPrimary,
        borderRadius: "12px",
        overflow: "hidden",
        minWidth: "200px",
        maxWidth: "260px",
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.3)"
          : "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      {/* Compact header with close */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${navyColor} 0%, #1a4a6a 100%)`,
          padding: "4px 8px 5px",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: `linear-gradient(90deg, ${greenColor}, transparent)`,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minHeight: 24 }}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {tag || assetTag || "Unknown Asset"}
          </Typography>
          {actualBattery !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: "4px",
                padding: "2px 4px",
                flexShrink: 0,
              }}
            >
              <Box sx={{ color: getBatteryColor(actualBattery), display: "flex" }}>{getBatteryIcon(actualBattery)}</Box>
              <Typography sx={{ color: "#fff", fontSize: "0.625rem", fontWeight: 600 }}>{actualBattery}%</Typography>
            </Box>
          )}
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{
                padding: "2px",
                color: "rgba(255,255,255,0.85)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)", color: "#fff" },
              }}
            >
              <CloseIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: 0.25 }}>
          <LocationOnIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.65rem" }} />
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.6rem", fontWeight: 500 }}>
            {locationString}
          </Typography>
        </Box>
      </Box>

      {/* Compact info content */}
      <Box sx={{ padding: "4px 8px 6px" }}>
        {/* Last Event */}
        <InfoRow
          icon={<EventIcon />}
          label="Last Event"
          value={lastEvent || event || "No event recorded"}
        />

        {/* Battery - only show if actual data available */}
        {actualBattery !== null && (
          <InfoRow
            icon={getBatteryIcon(actualBattery)}
            label="Battery Level"
            value={`${actualBattery}%`}
            valueColor={getBatteryColor(actualBattery)}
            chip
          />
        )}

        {/* Temperature - only show if actual data available */}
        {actualTemperature !== null && (
          <InfoRow
            icon={<DeviceThermostatIcon />}
            label="Temperature"
            value={`${actualTemperature}°`}
            valueColor={actualTemperature > 80 ? "#ef4444" : actualTemperature < 32 ? "#3b82f6" : textPrimary}
          />
        )}

        {/* Last Seen */}
        {(timeStamp || lastSeen) && (
          <InfoRow
            icon={<HistoryIcon />}
            label="Last Seen"
            value={new Date(timeStamp || lastSeen).toLocaleString()}
          />
        )}

        {/* Coordinates */}
        {latitude && longitude && (
          <InfoRow
            icon={<LocationOnIcon />}
            label="Coordinates"
            value={`${Number(latitude).toFixed(4)}, ${Number(longitude).toFixed(4)}`}
          />
        )}
      </Box>

      {/* Compact action row */}
      <Box sx={{ padding: "4px 8px 6px", display: "flex", gap: "4px", borderTop: `1px solid ${borderColor}` }}>
        <ActionButton icon={<OpenInNewIcon />} label={buttonLabel} onClick={onButtonClick} variant="contained" />
        <ActionButton icon={<NavigationIcon />} label="Navigate" onClick={() => onNavigateButtonClick(item)} />
      </Box>
    </Box>
  );
}
